import { createReducer } from '@reduxjs/toolkit';

import { IAction } from '../../types/redux';
import { ILayoutRestoreAction, LAYOUT_RESTORE } from '../layout';

import { getSidebarDefaultState } from './general.helpers';
import {
  GENERAL_SET,
  GENERAL_SET_IS_COMPILED,
  IGeneralState,
  IGeneralSetIsCompiledAction,
  GENERAL_SET_IS_SIDEBAR_OPEN,
  IGeneralSetIsSidebarOpenAction,
  IGeneralSetIsSecondaryCompiledAction,
  GENERAL_SET_IS_SECONDARY_COMPILED,
  GENERAL_SET_IS_PREVIEWS_OPEN,
  IGeneralSetIsPreviewsOpenAction,
  GENERAL_SET_IS_TK_PANEL_OPEN,
  IGeneralSetIsTkPanelOpenAction,
  IGeneralSetAction,
} from './general.types';

const INITIAL_STATE: IGeneralState = {
  isCompiled: false,
  isSecondaryCompiled: true,
  isSidebarOpen: getSidebarDefaultState(),
  isPreviewsOpen: false,
  isTkPanelOpen: false,
};

/**
 * Обе панели — правый drawer, одновременно они наложатся. Инвариант «открыта
 * максимум одна» живёт здесь, а не в обработчиках кликов: обработчик легко
 * добавить третий, и он про правило не узнает.
 *
 * Применяется к УЖЕ СЛИТОМУ состоянию, а не к payload: частичный payload
 * («открой предпоказы») ничего не говорит про вторую панель, и наивное слияние
 * оставило бы открытыми обе. Побеждает та, про которую сказано в payload;
 * если сказано про обе — панель меток, произвольно, но детерминированно.
 */
function closeCompetingPanel(
  state: IGeneralState,
  payload?: IGeneralSetAction | null,
): IGeneralState {
  if (payload?.isTkPanelOpen === true) {
    return { ...state, isPreviewsOpen: false };
  }

  if (payload?.isPreviewsOpen === true) {
    return { ...state, isTkPanelOpen: false };
  }

  return state;
}

export const generalReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(GENERAL_SET, (state: IGeneralState, action: IAction<IGeneralSetAction>) => {
      // Тип действия исправлен: сеттер разливает весь payload, а объявлен был
      // как «только isCompiled» — слияние делало больше, чем обещал тип.
      return closeCompetingPanel({ ...state, ...action.payload }, action.payload);
    })
    .addCase(
      GENERAL_SET_IS_COMPILED,
      (state: IGeneralState, action: IAction<IGeneralSetIsCompiledAction>) => {
        state.isCompiled = action.payload?.isCompiled || false;
      },
    )
    .addCase(
      GENERAL_SET_IS_SECONDARY_COMPILED,
      (state: IGeneralState, action: IAction<IGeneralSetIsSecondaryCompiledAction>) => {
        state.isSecondaryCompiled = action.payload?.isSecondaryCompiled || false;
      },
    )
    .addCase(
      GENERAL_SET_IS_SIDEBAR_OPEN,
      (state: IGeneralState, action: IAction<IGeneralSetIsSidebarOpenAction>) => {
        state.isSidebarOpen = action.payload?.isSidebarOpen || false;
      },
    )
    .addCase(
      GENERAL_SET_IS_PREVIEWS_OPEN,
      (state: IGeneralState, action: IAction<IGeneralSetIsPreviewsOpenAction>) => {
        const isPreviewsOpen = action.payload?.isPreviewsOpen || false;

        return closeCompetingPanel({ ...state, isPreviewsOpen }, { isPreviewsOpen });
      },
    )
    .addCase(
      GENERAL_SET_IS_TK_PANEL_OPEN,
      (state: IGeneralState, action: IAction<IGeneralSetIsTkPanelOpenAction>) => {
        const isTkPanelOpen = action.payload?.isTkPanelOpen || false;

        return closeCompetingPanel({ ...state, isTkPanelOpen }, { isTkPanelOpen });
      },
    )
    .addCase(LAYOUT_RESTORE, (state: IGeneralState, action: IAction<ILayoutRestoreAction>) => {
      if (!action.payload?.layout) {
        return;
      }

      // Только режимы досок. isSidebarOpen остаётся глобальной настройкой.
      // Ни одна из панелей не восстанавливается: предпоказы ходят на сервер,
      // а панель меток хоть и локальна, но открытая при загрузке боковая
      // панель — сюрприз, которого автор не просил.
      state.isCompiled = action.payload.layout.isCompiled;
      state.isSecondaryCompiled = action.payload.layout.isSecondaryCompiled;
    });
});
